import { useChatMessageContext } from '@/context/ChatMessageContext';
import { askHeidiCitationClicked } from '@/lib/analytics/scribe/ask-ai';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  EvidenceSourceInteraction,
  EvidenceSourceSurface,
} from '@/models/analytics/features/evidence';
import { getFaviconUrl } from '@/utils/faviconCache';
import {
  createResponseProse,
  legacyMarkdownBase,
  resolveInternalHref,
  type MdComponentProps,
  type ProseDensity,
  type ResponseProseComponents,
} from '@heidi/answer-kit';
import { Badge } from '@heidi/design-system/badge';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { BookOpenIcon, ExternalLink } from 'lucide-react';
import { type ReactNode } from 'react';

// URL sanitization
const TRACKING_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;

const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    TRACKING_PARAMS.forEach((param) => urlObj.searchParams.delete(param));
    return urlObj.toString();
  } catch {
    return url;
  }
};

const getDomainFromHref = (href: string): string => {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

/**
 * EVI-1486: shared helper for inline-link click tracking.
 *
 * Fires both:
 *  - The unified `evidence_source_clicked` event with `chat_id` / `message_id`
 *    / `surface` / `interaction` so a single event answers "who clicked which
 *    source from which surface".
 *  - The legacy `askHeidiCitationClicked` (Segment + `evidence_clicked`)
 *    event for back-compat so existing dashboards keep working during the
 *    migration window.
 *
 * Pulls `chat_id` / `message_id` from `ChatMessageContext` (set by
 * `TextResponse` in `components.tsx`) and falls back to `getCurrentSessionId()`
 * for the session ID; intentionally does NOT gate on session existence so
 * sessionless surfaces (standalone Ask Heidi, shared chats) still emit the
 * event.
 */
const trackInlineMarkdownLinkClick = (params: {
  href: string;
  ctx: ReturnType<typeof useChatMessageContext>;
  interaction: EvidenceSourceInteraction;
  surface?: EvidenceSourceSurface;
}) => {
  const { href, ctx, interaction } = params;
  const surface: EvidenceSourceSurface =
    params.surface ?? ctx.defaultSurface ?? 'inline_markdown_link';

  const domain = getDomainFromHref(href);
  const sessionId = ctx.sessionId ?? getCurrentSessionId();

  // Unified event (EVI-1486)
  track({
    event_name: TrackingEventName.EvidenceSourceClicked,
    props: {
      source_url: href,
      source_domain: domain,
      surface,
      interaction,
      chat_id: ctx.chatId,
      message_id: ctx.messageId,
      session_id: sessionId,
    },
    options: { amplitude: false },
  });

  // Legacy event (kept until callers are migrated; emits to Segment +
  // Amplitude `evidence_clicked` per the askHeidiCitationClicked emit host).
  // Do NOT gate on sessionId — previously this was silently dropped for
  // standalone Ask Heidi / shared chats (EVI-1486 root cause for the
  // Western Kids Health complaint).
  askHeidiCitationClicked({
    citationUrl: href,
    citationTitle: '',
    sessionId: sessionId ?? '',
    citationSource: 'response',
  });
};

/**
 * Named React component for the Evidence-mode `<a>` renderer.
 *
 * Pulled out of the components object literal so `useChatMessageContext`
 * passes `react-hooks/rules-of-hooks` (lowercase keys like `a:` are not
 * recognized as components by the lint rule, even though Streamdown does
 * render them as such).
 *
 * Inline links that resolve to an in-app destination (via the kit's
 * `resolveInternalHref` seam, wired to the settings-modal protocol in
 * `src/composition/hosts/answer-kit/answer-kit-wiring.ts`) open it in place rather than in a new
 * tab — see the `fix/settings-modal-inline-link-interception` change that
 * landed in parallel with EVI-1486. External links fall through to the
 * analytics-tracked open-in-new-tab path.
 */
// eslint-disable-next-line react-refresh/only-export-components -- verbatim move out of eslint-ignored Chat
function EvidenceMarkdownLink({
  href,
  children,
}: {
  href: string;
  children?: ReactNode;
}) {
  const ctx = useChatMessageContext();
  const openInPlace = resolveInternalHref(href);
  if (openInPlace) {
    return (
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault();
          openInPlace();
        }}
        className="cursor-pointer break-words text-text-link underline hover:text-text-link-hover"
      >
        {children}
      </a>
    );
  }
  const cleanUrl = sanitizeUrl(href);
  const handleClick = () => {
    trackInlineMarkdownLinkClick({
      href: cleanUrl,
      ctx,
      interaction: 'open_in_new_tab',
    });
  };
  return (
    <a
      href={cleanUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words text-text-link underline hover:text-text-link-hover"
      onClick={handleClick}
    >
      {children}
    </a>
  );
}

/**
 * Named React component for the legacy-mode `<a>` renderer (citation badge
 * with favicon + tooltip "Go to source" arrow).
 *
 * Pulled out of the `mdComponents` object literal for the same reason as
 * `EvidenceMarkdownLink` above (rules-of-hooks doesn't recognize lowercase
 * keys like `a:` as components).
 *
 * Tracking (EVI-1486):
 * - The badge click and the tooltip's "Go to source" arrow click both fire
 *   analytics. Neither path gates on session existence so sessionless
 *   surfaces (standalone Ask Heidi, shared chats) still emit the event.
 *
 * In-app destinations are intercepted via `resolveInternalHref` for parity
 * with `EvidenceMarkdownLink` and the kit's inline-context renderer.
 */
// eslint-disable-next-line react-refresh/only-export-components -- verbatim move out of eslint-ignored Chat
function LegacyMarkdownLink({ className, children, href, ...props }: MdComponentProps) {
  const ctx = useChatMessageContext();
  const openInPlace = resolveInternalHref(href as string);
  if (openInPlace) {
    return (
      <a
        href={href as string}
        onClick={(e) => {
          e.preventDefault();
          openInPlace();
        }}
        className="cursor-pointer break-words text-text-link underline hover:text-text-link-hover"
      >
        {children}
      </a>
    );
  }
  const cleanUrl = sanitizeUrl(href as string);

  const handleClick = () => {
    trackInlineMarkdownLinkClick({
      href: cleanUrl,
      ctx,
      interaction: 'open_in_new_tab',
    });
  };

  const handleTooltipClick = () => {
    trackInlineMarkdownLinkClick({
      href: cleanUrl,
      ctx,
      interaction: 'tooltip_go_to_source',
    });
    window.open(cleanUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span>
            <a
              className={cn('inline-block', className)}
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClick}
              {...props}
            >
              <Badge
                color="neutral"
                className="cursor-pointer gap-1 bg-background-tertiary"
                size="sm"
              >
                <img
                  src={getFaviconUrl(cleanUrl)}
                  alt=""
                  className="size-3 rounded-sm"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'inline-block';
                  }}
                />
                <BookOpenIcon className="hidden size-3" />
                <span className="">{children}</span>
              </Badge>
            </a>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="flex cursor-pointer items-center gap-1.5"
          onClick={handleTooltipClick}
        >
          <TooltipArrow />
          <span>Go to source</span>
          <ExternalLink className="size-3" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * The Evidence answer prose map, at a given column density.
 *
 * Nothing here is Evidence-specific except the `<a>` renderer — inline links
 * track source clicks and intercept internal settings routes, which needs
 * `ChatMessageContext` and so cannot live in the shared module. Everything else
 * (scale, weights, line heights, block margins, tables) comes from
 * `createResponseProse`, which Work renders from too.
 *
 * Built once per density at module scope so the map stays referentially stable
 * across renders: Streamdown re-renders its content whenever the `components`
 * reference changes.
 */
const buildEvidenceProse = (density: ProseDensity): ResponseProseComponents =>
  createResponseProse({
    density,
    tintedCell: true,
    linkComponent: ({ children, href }) => (
      <EvidenceMarkdownLink href={href as string}>{children}</EvidenceMarkdownLink>
    ),
  });

const EVIDENCE_PROSE_BY_DENSITY: Record<ProseDensity, ResponseProseComponents> = {
  comfortable: buildEvidenceProse('comfortable'),
  compact: buildEvidenceProse('compact'),
};

export const getEvidenceMdComponents = (density: ProseDensity): ResponseProseComponents =>
  EVIDENCE_PROSE_BY_DENSITY[density];

/**
 * The wide-column Evidence map, for a caller rendering into a document column.
 * Anything rendering into a narrow panel should call
 * `getEvidenceMdComponents('compact')` instead.
 */
export const evidenceMdComponents = getEvidenceMdComponents('comfortable');

export const mdComponents = {
  ...legacyMarkdownBase,
  a: (props: MdComponentProps) => <LegacyMarkdownLink {...props} />,
};
